import { useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { getISOWeek, getISOWeeksInYear, parseISO } from 'date-fns'

import { GeblokkeerdeWekenWarning } from '@components/GeblokkeerdeWekenWarning'
import { StartUitvoeringWarning } from '@components/StartUitvoeringWarning'
import {
  beginningOfYearDateForISOWeek,
  formatWeekYearToISOWeek,
} from '@util/date-util'

import { SelectString } from '../../../../core/components/SelectString'
import type { GeblokkeerdeWeek } from '../../../../planning/types/GeblokkeerdeWeek'
import { usePlanningPreview } from '../../hooks/usePlanningPreview'
import { useSaveEvent } from '../../hooks/useSaveEvent'
import type { HoofdleidingProject } from '../../types/HoofdleidingProject'
import {
  isHoofdleidingHerplanDoorlooptijdStatus,
  isHoofdleidingHerplanStatus,
} from '../../util/herplanning'
import { PlanningPreviewInfo } from '../PlanningPreviewInfo'

const MAX_WEEKS_IN_YEAR = 53
const HERPLAN_REDENEN = [
  'Leveringsproblemen materiaal',
  'Capaciteit aannemer',
  'Gevolg van conditionerende onderzoeken',
  'Tijdigheid vergunningstraject',
  'Planning klant / gemeente',
  'Uitvoeringsweek bekend',
  'Voorbereiding netbeheerder',
  'Voorbereiding aannemer',
  'Montagecapaciteit netbeheerder',
  'Tijdigheid voorgaand project',
  'Weersomstandigheden',
  'Externe omstandigheden',
]

const WEEK_NUMBER_53 = 53

const isValidWeek = (jaar: Date | null, week?: number) =>
  !!week &&
  week >= 1 &&
  week <= MAX_WEEKS_IN_YEAR &&
  (week !== WEEK_NUMBER_53 ||
    !jaar ||
    getISOWeeksInYear(jaar) === MAX_WEEKS_IN_YEAR)

type Props = {
  project: HoofdleidingProject
  handleClose: () => void
  geblokkeerdeWeken: GeblokkeerdeWeek[]
}

export const Herplan = ({ project, handleClose, geblokkeerdeWeken }: Props) => {
  const { mutate: saveEvent } = useSaveEvent()

  const {
    aanvraagID,
    startUitvoeringWeek,
    doorlooptijdWeken: doorlooptijd,
    statusText,
  } = project

  const canChangeStartuitvoering = isHoofdleidingHerplanStatus(statusText)
  const canChangeDoorlooptijd =
    isHoofdleidingHerplanDoorlooptijdStatus(statusText)

  const [jaar, setJaar] = useState<Date | null>(
    startUitvoeringWeek
      ? beginningOfYearDateForISOWeek(startUitvoeringWeek)
      : null,
  )
  const [week, setWeek] = useState<number | undefined>(
    startUitvoeringWeek ? getISOWeek(parseISO(startUitvoeringWeek)) : undefined,
  )
  const [doorlooptijdWeken, setDoorlooptijdWeken] = useState(doorlooptijd)
  const [herplannenBijlagen, setHerplannenBijlagen] = useState(
    canChangeStartuitvoering,
  )
  const [reden, setReden] = useState('')
  const [errors, setErrors] = useState({})
  const [text, setText] = useState('')

  const validate = () => {
    let doorlooptijdWekenError = false
    let startUitvoeringWeekError = false
    const redenError = startUitvoeringWeek && !reden

    const hasWeekChanged =
      !!week &&
      !!startUitvoeringWeek &&
      week !== getISOWeek(parseISO(startUitvoeringWeek))

    const hasDoorlooptijdChanged =
      !!doorlooptijdWeken && doorlooptijdWeken !== doorlooptijd

    if (hasWeekChanged || week === WEEK_NUMBER_53) {
      startUitvoeringWeekError = !isValidWeek(jaar, week)
    }

    if (hasDoorlooptijdChanged) {
      doorlooptijdWekenError = doorlooptijdWeken < 1
    }

    setErrors({
      redenError,
      doorlooptijdWekenError,
      startUitvoeringWeekError,
    })

    return !(doorlooptijdWekenError || startUitvoeringWeekError || redenError)
  }

  const { data: planningPreview, isCalculating } = usePlanningPreview({
    id: aanvraagID,
    startUitvoeringWeek:
      jaar && isValidWeek(jaar, week)
        ? formatWeekYearToISOWeek(jaar, week)
        : undefined,
    doorlooptijdWeken:
      doorlooptijdWeken && doorlooptijdWeken >= 1
        ? doorlooptijdWeken
        : undefined,
  })

  const handleSubmitClick = () => {
    if (validate()) {
      saveEvent(
        {
          id: aanvraagID,
          data: {
            // The API removes the week only when it is omitted, not when ''
            startUitvoeringWeek:
              formatWeekYearToISOWeek(jaar, week) || undefined,
            herplannenBijlagen,
            reden: startUitvoeringWeek ? reden : 'Uitvoeringsweek bekend',
            doorlooptijdWeken,
            text,
          },
          type: 'herplanning',
          source: 'manual',
        },
        {
          onSuccess: () => handleClose(),
        },
      )
    }
  }

  const handleVerwijderPlanningClick = () => {
    saveEvent(
      {
        id: aanvraagID,
        data: {
          herplannenBijlagen,
          reden: 'Correctie',
        },
        type: 'herplanning',
        source: 'manual',
      },
      { onSuccess: () => handleClose() },
    )
  }

  return (
    <>
      <DialogTitle>Herplannen project {aanvraagID}</DialogTitle>

      <DialogContent>
        <StartUitvoeringWarning jaar={jaar} week={week} />

        <GeblokkeerdeWekenWarning
          isoWeek={formatWeekYearToISOWeek(jaar, week || null)}
          geblokkeerdeWeken={geblokkeerdeWeken}
          gebiedscode={project.gebiedscode}
        />

        <PlanningPreviewInfo planningPreview={planningPreview} />

        <Box sx={{ display: 'flex', pb: 1 }}>
          <DatePicker
            views={['year']}
            label="Jaar start uitvoering"
            value={jaar}
            onChange={setJaar as any}
            openTo="year"
            slotProps={{
              field: { clearable: true },
              textField: {
                error: Boolean(errors['startUitvoeringJaarError']),
                variant: 'standard',
                sx: { width: '33%', pr: 1 },
              },
            }}
            disabled={!canChangeStartuitvoering}
          />
          <TextField
            sx={{ width: '33%', pr: 1 }}
            variant="standard"
            type="number"
            value={week || ''}
            label={'Week start uitvoering'}
            onChange={(e) => setWeek(Number(e.target.value))}
            error={errors['startUitvoeringWeekError']}
            disabled={!canChangeStartuitvoering}
            slotProps={{
              input: {
                inputProps: {
                  min: 1,
                  max: 52,
                  'data-testid': 'startUitvoeringWeek-field',
                },
              },
            }}
          />
          <TextField
            value={doorlooptijdWeken || ''}
            error={errors['doorlooptijdWekenError']}
            variant="standard"
            label={'Doorlooptijd'}
            onChange={(e) => setDoorlooptijdWeken(parseInt(e.target.value))}
            type="number"
            sx={{ width: '33%' }}
            disabled={!canChangeDoorlooptijd}
            slotProps={{
              input: {
                inputProps: {
                  min: 1,
                  'data-testid': 'doorlooptijdWeken-field',
                },
              },
            }}
          />
        </Box>
        {startUitvoeringWeek && (
          <>
            <SelectString
              label="Reden"
              value={reden}
              onChange={setReden}
              options={HERPLAN_REDENEN}
              error={errors['redenError']}
              disableClearable
            />
          </>
        )}

        {startUitvoeringWeek && (
          <FormControl variant="standard" sx={{ width: '100%', marginTop: 2 }}>
            <TextField
              value={text}
              multiline
              rows={6}
              label={`Toelichting (${text.length}/500)`}
              onChange={(e) => setText(e.target.value)}
              slotProps={{
                htmlInput: { maxLength: 500 },

                input: {
                  inputProps: { min: 1, 'data-testid': 'toelichting-field' },
                },
              }}
            />
          </FormControl>
        )}

        <Box sx={{ width: '90%', display: 'flex', mt: 2 }}>
          <Switch
            sx={{ mr: 1 }}
            checked={herplannenBijlagen}
            onChange={() => setHerplannenBijlagen(!herplannenBijlagen)}
            data-testid="bijlagen-switch"
            disabled={!canChangeStartuitvoering}
          />
          <Typography sx={{ alignSelf: 'center' }}>
            Nog op te leveren bijlagen en af te handelen activiteiten worden{' '}
            <strong>{herplannenBijlagen ? 'wel' : 'niet'}</strong> herpland.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        {project.statusText === 'Intake' && (
          <Button onClick={handleVerwijderPlanningClick} color="error">
            Verwijder planning
          </Button>
        )}
        <Button onClick={handleClose} color="primary" autoFocus>
          Annuleren
        </Button>
        <Button
          color="primary"
          onClick={handleSubmitClick}
          disabled={isCalculating}
        >
          Opslaan
        </Button>
      </DialogActions>
    </>
  )
}
